import moment from 'moment';
import {
  all,
  always,
  and,
  applySpec,
  compose,
  defaultTo,
  equals,
  gte,
  includes,
  indexOf,
  is,
  isEmpty,
  isNil,
  length,
  lte,
  map,
  mergeLeft,
  not,
  or,
  pluck,
  replace,
  slice,
  split,
  test,
  trim,
  when
} from 'ramda';
import { isNilOrEmpty, isNumber } from '@@serverHelpers/index.mjs';
import { isRequired } from './isRequired';
import * as EmailValidator from 'email-validator';
import iban from 'iban';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { trimNumber } from '@@viewHelpers/trimNumber.js';

export const nameIsValid = (mandatory = true) => {
  const stringRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/u;

  return when(always(mandatory), mergeLeft(isRequired), {
    validate: (value) =>
      stringRegex.test(value) ||
      'Le format du champ renseigné n’est pas valide'
  });
};

export const isEmailValid = (email) => {
  const trimmedEmail = trim(email);
  return EmailValidator.validate(trimmedEmail);
};

export const areEmailsValid = (email) =>
  compose(all(EmailValidator.validate), map(trim), split(';'))(email);

export const isSSKeyValid = (mandatory = true) => {
  return {
    ...(mandatory ? { required: isRequired } : null),
    validate: (value) =>
      (!mandatory && length(value) === 0) ||
      (equals(2, length(value)) && isNumber(parseInt(value))) ||
      ' '
  };
};

const validateNIR = (number, birthDate) => {
  const getFormattedBirthDate = (format) =>
    moment(birthDate, 'DD/MM/YYYY').format(format);

  if (!number) {
    return true;
  }

  const splitNumber = applySpec({
    key: slice(-2, Infinity),
    nir: slice(0, -2)
  });

  let { key, nir } = compose(splitNumber, trimNumber)(number);

  if (equals(13, length(nir))) {
    // Check if nir has letter for Corse
    if (equals(5, indexOf('2B', nir))) {
      nir = replace('2B', 18, nir);
    } else if (equals(5, indexOf('2A', nir))) {
      nir = replace('2A', 19, nir);
    }
    const birthYearMonth = getFormattedBirthDate('YYMM');

    const isNIRDateRight = equals(1, indexOf(birthYearMonth, nir));

    if (or(isNIRDateRight, !birthDate)) {
      const luhn = (value) => 97 - (parseInt(value) % 97);

      return equals(parseInt(key), luhn(nir));
    }
  }

  return isNilOrEmpty(number);
};

export const isValidSocialNumber = (birthDate, mandatory = true) => {
  return {
    ...(mandatory ? { required: isRequired } : null),
    validate: (value) => {
      if (isNilOrEmpty(value) && mandatory) {
        return isRequired.required;
      }
      return (
        validateNIR(value, birthDate) ||
        'Le format du champ saisi est invalide. Le n°SS est composé de 13 chiffres et est complété par une clé de contrôle composée de 2 chiffres'
      );
    }
  };
};

export const isValidContractNumber = (contractNumber) => {
  const contractNumberRegex = /^[A-Za-z0-9-]+$/;

  return contractNumberRegex.test(contractNumber);
};

export const isRequiredBoolean = (mandatory = true) => {
  return {
    ...(mandatory ? { required: isRequired } : null),
    validate: (value) => {
      if (not(is(Boolean, value)) && mandatory) {
        return isRequired.required;
      }
      return true;
    }
  };
};

export const isRequiredBooleanTrue = (mandatory = true) => {
  return {
    ...(mandatory ? { required: isRequired } : null),
    validate: (value) => {
      if (!value && mandatory) {
        return isRequired.required;
      }
      return true;
    }
  };
};

export const isRequiredSelect = (options = [], mandatory = true) => {
  const normalizedOptions = map(
    (value) => {
      if (is(String, value)) {
        return value.toLowerCase();
      }
      return is(Boolean, value) ? String(value) : '';
    },
    pluck('value', defaultTo([], options))
  );

  return {
    ...(mandatory ? { required: isRequired } : null),
    validate: (value) => {
      let normalizedValue = '';
      if (is(String, value)) {
        normalizedValue = value.toLowerCase();
      } else if (is(Boolean, value)) {
        normalizedValue = String(value);
      }

      const isValid = includes(normalizedValue, normalizedOptions);

      if (
        (!isValid || isNil(normalizedValue) || isEmpty(normalizedValue)) &&
        mandatory
      ) {
        return 'Ce champ est requis';
      }
      return true;
    }
  };
};

export const isRequiredInput = (mandatory = true) => {
  return {
    ...(mandatory ? { required: isRequired } : null),
    validate: (value) => {
      if ((isNil(value) || isEmpty(value)) && mandatory) {
        return isRequired.required;
      }
      return true;
    }
  };
};

export const isEmailValidNotRequired = () => {
  return {
    validate: (value) =>
      isNilOrEmpty(value) ||
      isEmailValid(value) ||
      'Le format de l\'email saisi est invalide.'
  };
};

export const isEmailValidAndRequired = {
  ...isRequired,
  validate: (value) =>
    isEmailValid(value) || 'Le format de l\'email saisi est invalide.'
};

export const isEmailValidAndRequiredOrTwo = {
  ...isRequired,
  validate: (value) => {
    const parts = value
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean);
    return (
      parts.every((email) => isEmailValid(email)) ||
      'Le format de l\'email saisi est invalide.'
    );
  }
};

export const areEmailsValidAndRequired = {
  ...isRequired,
  validate: (value) =>
    areEmailsValid(value) ||
    'Le format d\'un ou plusieurs emails saisis est invalide.'
};

export const isPhoneNumberValid = (required) => ({
  ...(required && {
    required: 'Ce champ est requis'
  }),
  validate: (value) =>
    (!value && !required) ||
    isValidPhoneNumber(value, 'FR') ||
    'Le format du numéro de téléphone saisi est invalide. Il doit comporter 10 chiffres.'
});

export const isIBANValid = (mandatory = true) => {
  return when(always(mandatory), mergeLeft(isRequired), {
    validate: (v) => {
      if (v) {
        return iban.isValid(v) || 'Le format saisi est invalide';
      }
    }
  });
};

export const isBICValid = (mandatory = true) => {
  const BICRegex = /^(?:[a-zA-Z0-9]{8}|[a-zA-Z0-9]{11})$/;

  return when(always(mandatory), mergeLeft(isRequired), {
    validate: (value) => {
      if (value) {
        return BICRegex.test(value) || 'Le format saisi est invalide';
      }
    }
  });
};

export const mobilePhoneNumberRegex = /^0[6-7][0-9]{8}$/;
export const landlinePhoneNumberRegex = /^0[1-589][0-9]{8}$/;

export const isLandlinePhoneNumberValid = (required = true) => {
  return when(always(required), mergeLeft(isRequired), {
    validate: (value) =>
      (!isNil(value) && value.length
        ? landlinePhoneNumberRegex.test(value)
        : !required) || 'Le format du N°tel fixe saisi est invalide'
  });
};

export const isMobilePhoneNumberValid = (required = true) => {
  return when(always(required), mergeLeft(isRequired), {
    validate: (value) =>
      (!isNil(value) && value.length
        ? mobilePhoneNumberRegex.test(value)
        : !required) || 'Le format du N°tel mobile saisi est invalide'
  });
};

export const numberIsBetween = (
  minNumber,
  maxNumber,
  message,
  mandatory = true
) => {
  return when(always(mandatory), mergeLeft(isRequired), {
    validate: (number) => {
      if (isNilOrEmpty(number) && !mandatory) {
        return true;
      } else if (and(equals(maxNumber, -1), equals(minNumber, -1))) {
        return true;
      } else if (equals(minNumber, -1)) {
        return lte(number, maxNumber) || message;
      } else if (equals(maxNumber, -1)) {
        return gte(number, minNumber) || message;
      } else {
        return and(gte(number, minNumber), lte(number, maxNumber)) || message;
      }
    }
  });
};

const validateSirenSiret = (number) => {
  if (isNil(number) || isEmpty(number)) {
    return true;
  }

  const formattedNumber = replace(/\s/g, '')(number);
  const isNumeric = test(/^\d+$/);
  const numLength = length(formattedNumber);

  return isNumeric(formattedNumber) && includes(numLength, [9, 14]);
};

export const isValidSirenSiret = (mandatory = true) => {
  return {
    ...(mandatory ? { required: 'Ce champ est requis' } : {}),
    validate: (value) => {
      if (isNil(value) || isEmpty(value)) {
        return mandatory ? 'Ce champ est requis' : true;
      }
      return (
        validateSirenSiret(value) ||
        'SIREN/SIRET : le format attendu n\'est pas respecté. Veuillez modifier votre saisie.'
      );
    }
  };
};

export const isValidEmployerPercentShare = (mandatory = true) => {
  return {
    ...(mandatory ? { required: 'Ce champ est requis' } : {}),
    validate: (value) => {
      if (isNil(value) || isEmpty(value)) {
        return mandatory ? 'Ce champ est requis' : true;
      }
      const numericValue = parseInt(value.replace(/\D/g, ''), 10);
      if (isNaN(numericValue)) return 'Valeur invalide';
      if (numericValue !== 0 && (numericValue < 50 || numericValue > 100)) {
        return 'La part employeur doit être à 0% ou comprise entre 50% et 100%';
      }
      return true;
    }
  };
};

const validateCompanyName = (name) => {
  if (isNil(name) || isEmpty(name)) {
    return true;
  }

  return length(name) <= 50;
};

export const isValidCompanyName = (mandatory = true) => {
  return {
    ...(mandatory ? { required: 'Ce champ est requis' } : {}),
    validate: (value) => {
      if (isNil(value) || isEmpty(value)) {
        return mandatory ? 'Ce champ est requis' : true;
      }
      return (
        validateCompanyName(value) ||
        'Nom de l\'entreprise : format alphanumérique de 50 caractères maximum attendu.'
      );
    }
  };
};

const validateNafApe = (code) => {
  if (isNil(code) || isEmpty(code)) {
    return true;
  }

  // Check if the code is a valid NAF/APE code => ex: 01.11Z
  const isValidFormat = test(/^\d{2}\.\d{2}[A-Z]?$/i);

  return isValidFormat(code);
};

// when in use add mask="99.99a" in ControlledInput
export const isValidNafApe = (mandatory = true) => {
  return {
    ...(mandatory ? { required: 'Ce champ est requis' } : {}),
    validate: (value) => {
      if (isNil(value) || isEmpty(value)) {
        return mandatory ? 'Ce champ est requis' : true;
      }
      return (
        validateNafApe(value) ||
        'NAF/APE : le format attendu n\'est pas respecté (AA.AAB). Veuillez modifier votre saisie.'
      );
    }
  };
};
